import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { useLanguage } from '@/contexts/LanguageContext';
import { Star, Quote } from 'lucide-react';

const testimonials = [
  {
    name: 'Mohammad Ashraf',
    role: 'Owner, Ashraf Electronics',
    space: 'Shop A2 — 1st Floor',
    rating: 5,
    text: 'Khan Complex has been the perfect location for my electronics store. The high footfall and strategic location in Seer Hamdan have helped my business grow significantly. The management is professional and responsive.',
    avatar: 'MA',
  },
  {
    name: 'Dr. Faizan Ahmad',
    role: 'Medical Practitioner',
    space: 'Room B3 — 2nd Floor',
    rating: 5,
    text: 'I set up my clinic on the 2nd floor and the space is ideal for a professional practice. The complex is well-maintained, has reliable power backup, and patients find it convenient to reach. Highly recommended for healthcare professionals.',
    avatar: 'FA',
  },
  {
    name: 'Syed Imran Bukhari',
    role: 'Managing Director, Bukhari Textiles',
    space: 'Grand Hall H1',
    rating: 5,
    text: 'We hosted a textile exhibition in the Grand Hall and the space exceeded our expectations. The 72-foot hall provided ample room for 40+ stalls. The infrastructure and location made it a huge success. We plan to return quarterly.',
    avatar: 'SB',
  },
  {
    name: 'Bilal Ahmad Dar',
    role: 'Chartered Accountant',
    space: 'Room C1 — 3rd Floor',
    rating: 4,
    text: 'The 3rd floor office offers a quiet working environment with great views. Power backup means no disruption to client meetings. The complex is growing into a recognized business address in Anantnag district.',
    avatar: 'BD',
  },
  {
    name: 'Ruqaya Bano',
    role: 'Owner, Kashmir Handicrafts',
    space: 'Shop A5 — 1st Floor',
    rating: 5,
    text: 'My handicraft showroom at Khan Complex attracts tourists and locals alike. The parking facility is a big plus as customers can visit comfortably. The complex has truly become a commercial landmark in Seer Hamdan.',
    avatar: 'RB',
  },
  {
    name: 'Tariq Hussain Lone',
    role: 'Real Estate Investor',
    space: 'Multiple Units',
    rating: 5,
    text: 'I have invested in three commercial units at Khan Complex and the returns have been excellent. The occupancy rates are high, the management is transparent, and the property value continues to appreciate. A solid investment.',
    avatar: 'TL',
  },
];

function StarRating({ rating }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map(i => (
        <Star key={i} className={`w-3.5 h-3.5 ${i <= rating ? 'fill-[#D4AF37] text-[#D4AF37]' : 'text-[#475569]'}`} />
      ))}
    </div>
  );
}

export default function TestimonialsSection() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  const { t } = useLanguage();

  return (
    <section id="testimonials" data-testid="testimonials-section" className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#0B1120]">
      <div className="max-w-7xl mx-auto" ref={ref}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <p className="text-xs tracking-[0.2em] uppercase text-[#D4AF37] font-bold mb-4 font-['Outfit']">
            {t('testimonials.label')}
          </p>
          <h2 className="text-3xl sm:text-4xl font-light text-[#F8FAFC] font-['Cormorant_Garamond'] tracking-tight">
            {t('testimonials.title')}
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {testimonials.map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              data-testid={`testimonial-card-${i}`}
              className="group relative bg-[#0F172A] border border-white/5 rounded-lg p-7 hover:border-[#D4AF37]/30 transition-all duration-300"
            >
              {/* Quote icon */}
              <Quote className="w-8 h-8 text-[#D4AF37]/15 absolute top-5 right-5" />

              {/* Rating */}
              <StarRating rating={item.rating} />

              {/* Text */}
              <p className="text-sm text-[#94A3B8] font-['Outfit'] leading-relaxed mt-4 mb-6">
                "{item.text}"
              </p>

              {/* Author */}
              <div className="flex items-center gap-3 pt-4 border-t border-white/5">
                <div className="w-10 h-10 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/30 flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-bold text-[#D4AF37] font-['Outfit']">{item.avatar}</span>
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-[#F8FAFC] font-['Outfit'] truncate">{item.name}</p>
                  <p className="text-xs text-[#94A3B8] font-['Outfit'] truncate">{item.role}</p>
                  <p className="text-[10px] text-[#D4AF37]/80 font-['Outfit'] mt-0.5">{item.space}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
